import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ScheduleSection = ({ formData, setFormData, errors }) => {
  const eventTypeOptions = [
    { value: 'one-time', label: 'One-time Event' },
    { value: 'recurring', label: 'Recurring Sessions' },
    { value: 'ongoing', label: 'Ongoing Service' },
    { value: 'course', label: 'Multi-week Course' }
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'fortnightly', label: 'Fortnightly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev?.schedule,
        [field]: value
      }
    }));
  };

  const handleEventTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev?.schedule,
        eventType: value,
        // Reset fields when type changes
        ...(value !== 'recurring' && { frequency: '', endDate: '' }),
        ...(value !== 'course' && { courseWeeks: '', courseStartDate: '' }),
        ...(value === 'ongoing' && { startDate: '', endDate: '', startTime: '', endTime: '' })
      }
    }));
  };

  return (
    <div className="bg-card rounded-lg p-6 space-y-6">
      <div className="border-b border-border pb-4">
        <h2 className="text-xl font-heading font-semibold text-foreground">Schedule & Timing</h2>
        <p className="text-sm text-muted-foreground mt-1">When does your service or event take place?</p>
      </div>
      <div className="space-y-6">
        <div>
          <Select
            label="Event Type"
            placeholder="Select event type"
            options={eventTypeOptions}
            value={formData?.schedule?.eventType}
            onChange={handleEventTypeChange}
            error={errors?.['schedule.eventType']}
            required
          />
        </div>

        {formData?.schedule?.eventType === 'one-time' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Input
                label="Event Date"
                type="date"
                value={formData?.schedule?.startDate}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                error={errors?.['schedule.startDate']}
                required
              />
            </div>
            <div>
              <Input
                label="Start Time"
                type="time"
                value={formData?.schedule?.startTime}
                onChange={(e) => handleInputChange('startTime', e?.target?.value)}
                error={errors?.['schedule.startTime']}
                required
              />
            </div>
            <div>
              <Input
                label="End Time"
                type="time"
                value={formData?.schedule?.endTime}
                onChange={(e) => handleInputChange('endTime', e?.target?.value)}
                error={errors?.['schedule.endTime']}
              />
            </div>
            <div>
              <Input
                label="Duration (minutes)"
                type="number"
                placeholder="90"
                value={formData?.schedule?.duration}
                onChange={(e) => handleInputChange('duration', e?.target?.value)}
                description="Approximate session length"
              />
            </div>
          </div>
        )}

        {formData?.schedule?.eventType === 'recurring' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Select
                label="Frequency"
                placeholder="How often?"
                options={frequencyOptions}
                value={formData?.schedule?.frequency}
                onChange={(value) => handleInputChange('frequency', value)}
                error={errors?.['schedule.frequency']}
                required
              />
            </div>
            <div>
              <Input
                label="Start Date"
                type="date"
                value={formData?.schedule?.startDate}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                error={errors?.['schedule.startDate']}
                required
              />
            </div>
            <div>
              <Input
                label="Start Time"
                type="time"
                value={formData?.schedule?.startTime}
                onChange={(e) => handleInputChange('startTime', e?.target?.value)}
                error={errors?.['schedule.startTime']}
                required
              />
            </div>
            <div>
              <Input
                label="Duration (minutes)"
                type="number"
                placeholder="60"
                value={formData?.schedule?.duration}
                onChange={(e) => handleInputChange('duration', e?.target?.value)}
                required
              />
            </div>
            <div className="lg:col-span-2">
              <Input
                label="End Date (Optional)"
                type="date"
                value={formData?.schedule?.endDate}
                onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                description="Leave blank if ongoing indefinitely"
              />
            </div>
          </div>
        )}

        {formData?.schedule?.eventType === 'course' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Input
                label="Course Start Date"
                type="date"
                value={formData?.schedule?.courseStartDate}
                onChange={(e) => handleInputChange('courseStartDate', e?.target?.value)}
                error={errors?.['schedule.courseStartDate']}
                required
              />
            </div>
            <div>
              <Input
                label="Number of Weeks"
                type="number"
                placeholder="8"
                value={formData?.schedule?.courseWeeks}
                onChange={(e) => handleInputChange('courseWeeks', e?.target?.value)}
                error={errors?.['schedule.courseWeeks']}
                required
              />
            </div>
            <div>
              <Input
                label="Session Time"
                type="time"
                value={formData?.schedule?.startTime}
                onChange={(e) => handleInputChange('startTime', e?.target?.value)}
                error={errors?.['schedule.startTime']}
                required
              />
            </div>
            <div>
              <Input
                label="Session Duration (minutes)"
                type="number"
                placeholder="90"
                value={formData?.schedule?.duration}
                onChange={(e) => handleInputChange('duration', e?.target?.value)}
                required
              />
            </div>
          </div>
        )}

        {formData?.schedule?.eventType === 'ongoing' && (
          <div className="bg-accent/20 border border-accent/30 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 bg-accent rounded-full flex items-center justify-center mt-0.5">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
              <div>
                <h3 className="font-body font-medium text-foreground">Ongoing Service</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Your listing will be marked as an ongoing service available by appointment or arrangement.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <Input
            label="Capacity"
            type="number"
            placeholder="12"
            value={formData?.schedule?.capacity}
            onChange={(e) => handleInputChange('capacity', e?.target?.value)}
            description="Maximum number of participants (leave blank for unlimited)"
          />

          <Checkbox
            label="Booking required"
            description="Participants must book in advance"
            checked={formData?.schedule?.bookingRequired}
            onChange={(e) => handleInputChange('bookingRequired', e?.target?.checked)}
          />

          <Checkbox
            label="Drop-ins welcome"
            description="People can attend without booking"
            checked={formData?.schedule?.dropInsWelcome}
            onChange={(e) => handleInputChange('dropInsWelcome', e?.target?.checked)}
          />
        </div>
      </div>
    </div>
  );
};

export default ScheduleSection;